<template>
<div class="pd-page">
  <div class="pd-header">
    <div class="pd-header-inner">
      <div class="pd-header-left">
        <div class="pd-parent-photo">
          <img v-if="parentPhoto" :src="parentPhoto" @click="showParentPhotoFull" />
          <span v-else class="pd-avatar-sm" @click="$refs.parentPhotoInput.click()">{{ parentName?.charAt(0) || '👤' }}</span>
          <div class="pd-parent-overlay" @click="parentPhoto ? showParentPhotoFull() : $refs.parentPhotoInput.click()">
            <span>{{ parentPhoto ? '🔍' : '📷' }}</span>
          </div>
          <div class="pd-photo-edit" @click.stop="$refs.parentPhotoInput.click()">📷</div>
        </div>
        <div>
          <h4 class="pd-title">{{ parentName || 'My Children' }}</h4>
          <p class="pd-subtitle">{{ multiSchool ? children.length + ' children across ' + schoolCount + ' schools' : institutionName }}</p>
        </div>
        <input ref="parentPhotoInput" type="file" accept="image/*" capture="user" style="display:none" @change="uploadParentPhoto" />
      </div>
      <div class="pd-header-actions">
        <button v-if="userRole !== 'parent'" class="pd-btn-sm pd-btn-outline" @click="$router.push('/dashboard')">← Admin</button>
        <button class="pd-logout" @click="logout">Logout</button>
      </div>
    </div>
  </div>

  <div class="pd-body">
    <div v-if="loading" class="pd-loading">Loading...</div>
    <div v-else-if="children.length === 0" class="pd-empty">
      <p>No children linked to your account yet.</p>
      <p class="pd-muted">Ask your school to add your phone number to your child's profile.</p>
    </div>

    <!-- Outstanding platform fees (Simi Trac per-term fee) -->
    <div v-if="platformInvoices.length > 0" class="pd-child-card" style="border:1px solid rgba(245,158,11,.4);background:linear-gradient(135deg,rgba(245,158,11,.05) 0%,rgba(15,35,71,1) 100%);">
      <h6 class="pd-section-title" style="margin-top:0;">🧾 Simi Trac Platform Fees</h6>
      <p class="pd-muted pd-small" style="margin-bottom:10px;">Per-term fee billed by Simi Trac. Either parent can clear it.</p>
      <div v-for="inv in platformInvoices" :key="inv.id" class="pd-txn-item" style="flex-wrap:wrap;gap:8px;">
        <div style="flex:1;min-width:200px;">
          <div style="color:#E8EDF4;font-weight:600;">{{ inv.student_name }} — {{ inv.period_label || 'Term fee' }}</div>
          <div class="pd-small pd-muted">{{ inv.institution_name }} · Due {{ formatDate(inv.due_date) }}</div>
        </div>
        <div style="text-align:right;">
          <div style="color:#F59E0B;font-weight:800;font-size:15px;">KSh {{ formatNum(inv.amount) }}</div>
        </div>
        <button class="pd-btn-sm pd-btn-green-sm" @click="payPlatformInvoice(inv)" :disabled="invoicePayingId === inv.id">
          {{ invoicePayingId === inv.id ? 'Sending STK...' : '💳 Pay via M-Pesa' }}
        </button>
      </div>
      <p v-if="invoiceMsg" :class="invoiceOk ? 'pd-c-green' : 'pd-c-red'" class="pd-small mt-2 tc">{{ invoiceMsg }}</p>
    </div>

    <div v-for="child in children" :key="child.id" class="pd-child-card">
      <div v-if="multiSchool" class="pd-school-badge">🏫 {{ child.institution_name }}</div>
      <div class="pd-child-header">
        <div class="pd-child-photo-wrap">
          <div class="pd-child-photo" @click="child.photo_url ? showPhoto(child) : null" :class="{'pd-clickable': child.photo_url}">
            <img v-if="child.photo_url" :src="child.photo_url" @error="$event.target.style.display='none'" />
            <span v-else class="pd-avatar">{{ child.name?.charAt(0) }}</span>
            <div v-if="child.photo_url" class="pd-photo-overlay"><span>🔍</span></div>
          </div>
          <button class="pd-photo-upload-btn" @click="triggerChildPhotoUpload(child)" title="Change photo">📷</button>
          <input :ref="'childPhoto_' + child.id" type="file" accept="image/*" capture="environment" style="display:none" @change="uploadChildPhoto($event, child)" />
        </div>
        <div class="pd-child-info">
          <h5 class="pd-child-name">{{ child.name }}</h5>
          <span class="pd-child-class">{{ child.admission_no }} · {{ child.class_name || 'No class' }}</span>
        </div>
        <div class="pd-wallet-badge">
          <span class="pd-wallet-label">Wallet</span>
          <span class="pd-wallet-amount">KSh {{ formatNum(child.wallet_balance) }}</span>
        </div>
      </div>

      <div class="pd-actions">
        <button v-if="child.wallet_type !== 'credit'" class="pd-action-btn pd-green" @click="showTopUp(child)">💰 Top Up</button>
        <div v-else class="pd-sponsored-chip" title="This is a sponsored wallet. Top-ups are handled by the sponsor — no need to load it yourself.">
          🎓 Sponsored
        </div>
        <button class="pd-action-btn pd-blue" @click="showWalletSettings(child)">⚙️ Wallet</button>
        <button class="pd-action-btn pd-purple" @click="showReports(child)">📊 Reports</button>
        <button class="pd-action-btn pd-orange" @click="showFees(child)">🎓 Fees</button>
      </div>

      <div class="pd-section">
        <h6 class="pd-section-title">📍 Today's Attendance</h6>
        <div v-if="child.attendance_today && child.attendance_today.length > 0" class="pd-att-list">
          <div v-for="(att, i) in child.attendance_today" :key="i" class="pd-att-item">
            <span>{{ att.tap_point_type === 'gate_entry' ? '🟢' : att.tap_point_type === 'gate_exit' ? '🔴' : '📍' }}</span>
            <span class="pd-att-point">{{ att.tap_point_name || att.tap_point_type }}</span>
            <span class="pd-att-time">{{ formatTime(att.tapped_at) }}</span>
          </div>
        </div>
        <p v-else class="pd-muted pd-small">No attendance recorded today</p>
      </div>

      <div class="pd-section" v-if="child.recent_transactions && child.recent_transactions.length > 0">
        <h6 class="pd-section-title">📈 Spending This Week</h6>
        <div class="pd-chart">
          <div v-for="(day, i) in getWeeklySpending(child)" :key="i" class="pd-chart-col">
            <div class="pd-chart-bar" :style="{height: day.pct + '%'}" :class="day.pct > 0 ? 'pd-bar-fill' : ''"></div>
            <span class="pd-chart-lbl">{{ day.label }}</span>
            <span v-if="day.total > 0" class="pd-chart-val">{{ day.total }}</span>
          </div>
        </div>
      </div>

      <div class="pd-section">
        <h6 class="pd-section-title">🎓 Fee Balance</h6>
        <div class="pd-fees-row">
          <div class="pd-fee-item"><span class="pd-fee-label">Total</span><span class="pd-fee-val">KSh {{ formatNum(child.fees?.total) }}</span></div>
          <div class="pd-fee-item"><span class="pd-fee-label">Paid</span><span class="pd-fee-val pd-c-green">KSh {{ formatNum(child.fees?.paid) }}</span></div>
          <div class="pd-fee-item"><span class="pd-fee-label">Balance</span><span class="pd-fee-val" :class="child.fees?.balance > 0 ? 'pd-c-red' : 'pd-c-green'">KSh {{ formatNum(child.fees?.balance) }}</span></div>
        </div>
        <button v-if="child.fees?.balance > 0" class="pd-action-btn pd-green pd-full mt-2" @click="showFees(child)">💳 Pay School Fees via M-Pesa</button>
      </div>

      <div class="pd-section" v-if="child.recent_transactions && child.recent_transactions.length > 0">
        <h6 class="pd-section-title">💳 Recent Transactions</h6>
        <div class="pd-txn-list">
          <div v-for="(txn, i) in child.recent_transactions.slice(0, 5)" :key="i" class="pd-txn-item">
            <span>{{ txn.type === 'debit' ? '🔻' : '🔹' }}</span>
            <span class="pd-txn-desc">{{ txn.description || txn.category }}</span>
            <span class="pd-txn-amt" :class="txn.type === 'debit' ? 'pd-c-red' : 'pd-c-green'">{{ txn.type === 'debit' ? '-' : '+' }}KSh {{ formatNum(txn.amount) }}</span>
            <span class="pd-txn-date">{{ formatDate(txn.created_at) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="pd-footer"><p>Powered by <strong>Simi Trac</strong> · simitrac.co.ke</p></div>

  <!-- Photo Modal (child) -->
  <div v-if="photoModal" class="pd-overlay" @click="photoModal=null">
    <div class="pd-modal pd-modal-photo" @click.stop>
      <button class="pd-x" @click="photoModal=null">✕</button>
      <img :src="photoModal.photo_url" class="pd-img-full" />
      <div class="pd-img-cap"><h5>{{ photoModal.name }}</h5><p>{{ photoModal.admission_no }}</p></div>
    </div>
  </div>

  <!-- Photo Modal (parent) -->
  <div v-if="parentPhotoFull" class="pd-overlay" @click="parentPhotoFull=false">
    <div class="pd-modal pd-modal-photo" @click.stop>
      <button class="pd-x" @click="parentPhotoFull=false">✕</button>
      <img :src="parentPhoto" class="pd-img-full" />
      <div class="pd-img-cap">
        <h5>{{ parentName }}</h5>
        <p>{{ userRole === 'parent' ? 'Parent' : userRole }}</p>
        <button class="pd-btn-sm pd-btn-outline mt-2" @click="parentPhotoFull=false; $refs.parentPhotoInput.click()">📷 Change Photo</button>
      </div>
    </div>
  </div>

  <!-- Top Up Modal -->
  <div v-if="topUpModal" class="pd-overlay" @click="topUpModal=null">
    <div class="pd-modal" @click.stop>
      <button class="pd-x" @click="topUpModal=null">✕</button>
      <h5 class="pd-mtitle">💰 Top Up Wallet</h5>
      <p class="pd-muted">{{ topUpModal.name }} — Balance: KSh {{ formatNum(topUpModal.wallet_balance) }}</p>
      <div class="pd-fg"><label>Amount (KSh)</label><input type="number" v-model.number="topUpAmount" placeholder="500" class="pd-inp" /></div>
      <div class="pd-fg"><label>M-Pesa Phone</label><input type="tel" v-model="topUpPhone" placeholder="0712345678" class="pd-inp" /></div>
      <div class="pd-quicks"><button v-for="a in [100,200,500,1000,2000]" :key="a" @click="topUpAmount=a" class="pd-qbtn">{{ a }}</button></div>
      <button class="pd-btn pd-btn-green pd-full" @click="doTopUp" :disabled="topUpLoading">{{ topUpLoading ? 'Sending STK...' : 'Pay via M-Pesa' }}</button>
      <p v-if="topUpMsg" :class="topUpOk ? 'pd-c-green' : 'pd-c-red'" class="pd-small mt-2 tc">{{ topUpMsg }}</p>
    </div>
  </div>

  <!-- Wallet Settings Modal -->
  <div v-if="walletModal" class="pd-overlay" @click="walletModal=null">
    <div class="pd-modal" @click.stop>
      <button class="pd-x" @click="walletModal=null">✕</button>
      <h5 class="pd-mtitle">⚙️ Wallet Settings</h5>
      <p class="pd-muted">{{ walletModal.name }}</p>
      <div class="pd-srow"><span>Daily Spend Limit</span><input type="number" v-model.number="ws.dailyLimit" placeholder="No limit" class="pd-inp-sm" /></div>
      <div class="pd-srow"><span>Weekly Spend Limit</span><input type="number" v-model.number="ws.weeklyLimit" placeholder="No limit" class="pd-inp-sm" /></div>
      <div class="pd-srow"><span>🔒 Freeze Wallet</span><label class="pd-sw"><input type="checkbox" v-model="ws.frozen" /><span class="pd-sw-s"></span></label></div>
      <div class="pd-srow"><span>Wallet PIN</span>
        <div class="pd-pin-actions">
          <button class="pd-btn-sm pd-btn-outline" @click="showSetPin = !showSetPin">🔑 Set PIN</button>
          <button class="pd-btn-sm pd-btn-outline" @click="resetPin">🔄 Reset</button>
        </div>
      </div>
      <div v-if="showSetPin" class="pd-pin-set">
        <div class="pd-pin-row">
          <input type="password" v-model="newPin" placeholder="Enter 4+ digit PIN" maxlength="6" class="pd-inp-sm" style="width:140px;text-align:center;letter-spacing:4px" />
          <button class="pd-btn-sm pd-btn-green-sm" @click="setManualPin" :disabled="pinLoading">{{ pinLoading ? '...' : 'Set' }}</button>
        </div>
        <p v-if="pinMsg" :class="pinOk ? 'pd-c-green' : 'pd-c-red'" class="pd-small">{{ pinMsg }}</p>
      </div>
      <button class="pd-btn pd-btn-blue pd-full mt-3" @click="saveWallet" :disabled="wLoading">{{ wLoading ? 'Saving...' : 'Save Settings' }}</button>
      <p v-if="wMsg" :class="wOk ? 'pd-c-green' : 'pd-c-red'" class="pd-small mt-2 tc">{{ wMsg }}</p>
    </div>
  </div>

  <!-- Reports Modal -->
  <div v-if="reportModal" class="pd-overlay" @click="reportModal=null">
    <div class="pd-modal pd-modal-report" @click.stop>
      <button class="pd-x" @click="reportModal=null;reportView=null">✕</button>
      <h5 class="pd-mtitle">📊 Reports</h5>
      <p class="pd-muted">{{ reportModal.name }} · {{ reportModal.institution_name || institutionName }}</p>

      <!-- Report Type Tabs -->
      <div class="pd-rtabs" v-if="!reportView">
        <button class="pd-rtab" @click="viewReport('attendance')">
          <span class="pd-rtab-icon">📍</span>
          <span class="pd-rtab-label">Attendance</span>
          <span class="pd-rtab-sub">Tap-in/out history</span>
        </button>
        <button class="pd-rtab" @click="viewReport('wallet')">
          <span class="pd-rtab-icon">💳</span>
          <span class="pd-rtab-label">Wallet Statement</span>
          <span class="pd-rtab-sub">All transactions</span>
        </button>
        <button class="pd-rtab" @click="viewReport('fees')">
          <span class="pd-rtab-icon">🎓</span>
          <span class="pd-rtab-label">Fee Statement</span>
          <span class="pd-rtab-sub">Fees & payments</span>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="rLoading" class="pd-rloading">
        <div class="pd-spinner"></div>
        <p>Loading report...</p>
      </div>

      <!-- Inline Report View -->
      <div v-if="reportView && !rLoading" class="pd-report-view">
        <div class="pd-report-toolbar">
          <button class="pd-btn-sm pd-btn-outline" @click="reportView=null;reportData=null">← Back</button>
          <div class="pd-report-dl">
            <button class="pd-btn-sm pd-btn-green-sm" @click="dlReport(reportView, 'pdf')">📄 PDF</button>
            <button class="pd-btn-sm pd-btn-outline" @click="dlReport(reportView, 'csv')">📊 CSV</button>
          </div>
        </div>

        <!-- ATTENDANCE REPORT -->
        <div v-if="reportView === 'attendance' && reportData" class="pd-rv-body">
          <div class="pd-rv-summary">
            <div class="pd-rv-stat"><span>Total Days</span><strong>{{ reportData.summary?.total_days || 0 }}</strong></div>
            <div class="pd-rv-stat"><span>Period</span><strong style="font-size:12px">{{ reportData.summary?.from || '—' }} to {{ reportData.summary?.to || '—' }}</strong></div>
          </div>
          <div v-if="reportData.attendance && reportData.attendance.length > 0" class="pd-rv-table">
            <div class="pd-rv-thead"><span>Date</span><span>Time</span><span>Type</span><span>Location</span></div>
            <div v-for="(a, i) in reportData.attendance" :key="i" class="pd-rv-trow" :class="i % 2 ? 'pd-rv-alt' : ''">
              <span>{{ (a.tapped_at||'').split(' ')[0] }}</span>
              <span>{{ formatTime(a.tapped_at) }}</span>
              <span>{{ a.tap_point_type === 'gate_entry' ? '🟢 In' : a.tap_point_type === 'gate_exit' ? '🔴 Out' : '📍 ' + a.tap_point_type }}</span>
              <span>{{ a.tap_point_name || '—' }}</span>
            </div>
          </div>
          <p v-else class="pd-muted pd-small tc">No attendance records found</p>
        </div>

        <!-- WALLET REPORT -->
        <div v-if="reportView === 'wallet' && reportData" class="pd-rv-body">
          <div class="pd-rv-summary">
            <div class="pd-rv-stat pd-rv-green"><span>Balance</span><strong>KSh {{ formatNum(reportData.summary?.balance) }}</strong></div>
            <div class="pd-rv-stat pd-rv-blue"><span>Loaded</span><strong>KSh {{ formatNum(reportData.summary?.total_loaded) }}</strong></div>
            <div class="pd-rv-stat pd-rv-orange"><span>Spent</span><strong>KSh {{ formatNum(reportData.summary?.total_spent) }}</strong></div>
          </div>
          <div v-if="reportData.transactions && reportData.transactions.length > 0" class="pd-rv-table">
            <div class="pd-rv-thead"><span>Date</span><span>Description</span><span>Amount</span></div>
            <div v-for="(t, i) in reportData.transactions" :key="i" class="pd-rv-trow" :class="i % 2 ? 'pd-rv-alt' : ''">
              <span class="pd-rv-date">{{ formatDate(t.created_at) }}</span>
              <span class="pd-rv-desc">{{ t.description || t.category || '—' }}</span>
              <span class="pd-rv-amt" :class="t.type === 'debit' ? 'pd-c-red' : 'pd-c-green'">{{ t.type === 'debit' ? '-' : '+' }}{{ formatNum(t.amount) }}</span>
            </div>
          </div>
          <p v-else class="pd-muted pd-small tc">No transactions found</p>
        </div>

        <!-- FEES REPORT -->
        <div v-if="reportView === 'fees' && reportData" class="pd-rv-body">
          <div class="pd-rv-summary">
            <div class="pd-rv-stat"><span>Total Fees</span><strong>KSh {{ formatNum(reportData.summary?.total_fees) }}</strong></div>
            <div class="pd-rv-stat pd-rv-green"><span>Paid</span><strong>KSh {{ formatNum(reportData.summary?.total_paid) }}</strong></div>
            <div class="pd-rv-stat" :class="reportData.summary?.outstanding > 0 ? 'pd-rv-red' : 'pd-rv-green'"><span>Outstanding</span><strong>KSh {{ formatNum(reportData.summary?.outstanding) }}</strong></div>
          </div>
          <div v-if="reportData.balances && reportData.balances.length > 0">
            <h6 class="pd-rv-sub">Fee Breakdown</h6>
            <div class="pd-rv-table">
              <div class="pd-rv-thead"><span>Term</span><span>Fees</span><span>Paid</span><span>Balance</span></div>
              <div v-for="(b, i) in reportData.balances" :key="i" class="pd-rv-trow" :class="i % 2 ? 'pd-rv-alt' : ''">
                <span>{{ b.term_name || '—' }}</span>
                <span>{{ formatNum(b.total_fees) }}</span>
                <span class="pd-c-green">{{ formatNum(b.total_paid) }}</span>
                <span :class="b.outstanding > 0 ? 'pd-c-red' : 'pd-c-green'">{{ formatNum(b.outstanding) }}</span>
              </div>
            </div>
          </div>
          <div v-if="reportData.payments && reportData.payments.length > 0">
            <h6 class="pd-rv-sub">Payment History</h6>
            <div class="pd-rv-table">
              <div class="pd-rv-thead"><span>Date</span><span>Amount</span><span>M-Pesa</span></div>
              <div v-for="(p, i) in reportData.payments" :key="i" class="pd-rv-trow" :class="i % 2 ? 'pd-rv-alt' : ''">
                <span>{{ p.transaction_date }}</span>
                <span class="pd-c-green" style="font-weight:600">KSh {{ formatNum(p.amount) }}</span>
                <span>{{ p.mpesa_code || '—' }}</span>
              </div>
            </div>
          </div>
          <p v-if="(!reportData.balances || reportData.balances.length === 0) && (!reportData.payments || reportData.payments.length === 0)" class="pd-muted pd-small tc">No fee records found</p>
        </div>
      </div>

      <p v-if="rMsg" class="pd-small mt-2 tc" :class="rMsg.startsWith('Error') ? 'pd-c-red' : 'pd-c-green'">{{ rMsg }}</p>
    </div>
  </div>

  <!-- Fees Modal -->
  <div v-if="feesModal" class="pd-overlay" @click="feesModal=null">
    <div class="pd-modal" @click.stop>
      <button class="pd-x" @click="feesModal=null">✕</button>
      <h5 class="pd-mtitle">🎓 Pay School Fees</h5>
      <p class="pd-muted">{{ feesModal.name }} — Outstanding: KSh {{ formatNum(feesModal.fees?.balance) }}</p>
      <div class="pd-fg"><label>Amount (KSh)</label><input type="number" v-model.number="feeAmt" :placeholder="feesModal.fees?.balance" class="pd-inp" /></div>
      <div class="pd-fg"><label>M-Pesa Phone</label><input type="tel" v-model="feePhone" placeholder="0712345678" class="pd-inp" /></div>
      <button class="pd-btn pd-btn-green pd-full" @click="doPayFees" :disabled="feeLoading">{{ feeLoading ? 'Sending STK...' : 'Pay via M-Pesa' }}</button>
      <p v-if="feeMsg" :class="feeOk ? 'pd-c-green' : 'pd-c-red'" class="pd-small mt-2 tc">{{ feeMsg }}</p>
    </div>
  </div>
</div>
</template>

<script>
/* eslint-disable */
import { executeJson, simitrack } from '@/api';
export default {
  name: 'ParentDashboard',
  data() {
    return {
      loading: true, children: [], photoModal: null, parentPhotoFull: false,
      institutionName: sessionStorage.getItem('institutionName') || 'School',
      userRole: sessionStorage.getItem('userRole') || 'parent',
      parentName: sessionStorage.getItem('userName') || '',
      parentPhoto: sessionStorage.getItem('parentPhoto') || null,
      topUpModal: null, topUpAmount: 500, topUpPhone: sessionStorage.getItem('userPhone') || '', topUpLoading: false, topUpMsg: '', topUpOk: false,
      walletModal: null, ws: { dailyLimit: null, weeklyLimit: null, frozen: false }, wLoading: false, wMsg: '', wOk: false,
      showSetPin: false, newPin: '', pinLoading: false, pinMsg: '', pinOk: false,
      reportModal: null, rMsg: '', reportView: null, reportData: null, rLoading: false,
      feesModal: null, feeAmt: 0, feePhone: sessionStorage.getItem('userPhone') || '', feeLoading: false, feeMsg: '', feeOk: false,
      platformInvoices: [], invoicePayingId: null, invoiceMsg: '', invoiceOk: false,
    };
  },
  mounted() { this.loadDashboard(); this.loadPlatformInvoices(); },
  computed: {
    multiSchool() { const schools = new Set(this.children.map(c => c.institution_id).filter(Boolean)); return schools.size > 1; },
    schoolCount() { return new Set(this.children.map(c => c.institution_id).filter(Boolean)).size; },
  },
  methods: {
    async loadDashboard() {
      this.loading = true;
      try {
        const res = await executeJson({ function: 'getParentDashboard' }, simitrack);
        if (res.data.success) { this.children = res.data.data?.children || []; if (res.data.data?.institution_name) this.institutionName = res.data.data.institution_name; if (this.children.length > 0 && this.children[0].institution_name) this.institutionName = this.children[0].institution_name; }
      } catch (e) { const s = sessionStorage.getItem('children'); if (s) try { this.children = JSON.parse(s); } catch (_) {} }
      this.loading = false;
    },
    showTopUp(c) { this.topUpModal = c; this.topUpMsg = ''; this.topUpAmount = 500; },

    // ========== Platform Invoices (per-term Simi Trac fee) ==========
    async loadPlatformInvoices() {
      try {
        const r = await executeJson({ function: 'listParentInvoices' }, simitrack);
        if (r.data.success) this.platformInvoices = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) { /* silent — not critical */ }
    },
    async payPlatformInvoice(inv) {
      const phone = sessionStorage.getItem('userPhone') || '';
      if (!phone) { this.invoiceMsg = 'No phone on your account — contact the school.'; this.invoiceOk = false; return; }
      this.invoicePayingId = inv.id; this.invoiceMsg = '';
      try {
        const r = await executeJson({ function: 'payParentInvoice', invoiceId: inv.id, phone }, simitrack);
        if (r.data.success) {
          this.invoiceMsg = r.data.message || 'STK sent — check your phone.';
          this.invoiceOk = true;
          // Optimistic refresh after a few seconds for callback to land
          setTimeout(() => this.loadPlatformInvoices(), 8000);
        } else {
          this.invoiceMsg = r.data.message || 'Payment failed';
          this.invoiceOk = false;
        }
      } catch (e) { this.invoiceMsg = 'Connection error'; this.invoiceOk = false; }
      this.invoicePayingId = null;
      setTimeout(() => { this.invoiceMsg = ''; }, 8000);
    },

    // ========== Photo Upload ==========
    triggerChildPhotoUpload(child) {
      const ref = this.$refs['childPhoto_' + child.id];
      if (ref) (Array.isArray(ref) ? ref[0] : ref).click();
    },
    async uploadChildPhoto(event, child) {
      const file = event.target.files[0];
      if (!file) return;
      const base64 = await this.fileToBase64(file);
      try {
        const res = await executeJson({ function: 'uploadStudentPhoto', studentId: child.id, photoBase64: base64 }, simitrack);
        if (res.data.success) { child.photo_url = res.data.data.photo_url; alert('Photo updated!'); }
        else alert(res.data.message || 'Upload failed');
      } catch (e) { alert('Upload error'); }
      event.target.value = '';
    },
    async uploadParentPhoto(event) {
      const file = event.target.files[0];
      if (!file) return;
      const base64 = await this.fileToBase64(file);
      try {
        const res = await executeJson({ function: 'uploadProfilePhoto', photoBase64: base64 }, simitrack);
        if (res.data.success) { this.parentPhoto = res.data.data.photo_url; sessionStorage.setItem('parentPhoto', res.data.data.photo_url); alert('Profile photo updated!'); }
        else alert(res.data.message || 'Upload failed');
      } catch (e) { alert('Upload error'); }
      event.target.value = '';
    },
    fileToBase64(file) {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas');
        const img = new Image();
        img.onload = () => {
          const maxSize = 600;
          let w = img.width, h = img.height;
          if (w > maxSize || h > maxSize) {
            if (w > h) { h = Math.round(h * maxSize / w); w = maxSize; }
            else { w = Math.round(w * maxSize / h); h = maxSize; }
          }
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.8).split(',')[1]);
        };
        img.src = URL.createObjectURL(file);
      });
    },
    async doTopUp() {
      if (!this.topUpAmount || this.topUpAmount < 10) { this.topUpMsg = 'Minimum KSh 10'; this.topUpOk = false; return; }
      if (!this.topUpPhone || this.topUpPhone.length < 9) { this.topUpMsg = 'Enter valid phone'; this.topUpOk = false; return; }
      this.topUpLoading = true; this.topUpMsg = '';
      try {
        const res = await executeJson({ function: 'walletTopUpStk', studentId: this.topUpModal.id, amount: this.topUpAmount, phone: this.topUpPhone }, simitrack);
        if (res.data.success) {
          const checkoutId = res.data.data?.checkout_request_id;
          this.topUpMsg = '📱 M-Pesa prompt sent! Enter your PIN...';
          this.topUpOk = true;
          this.topUpLoading = false;
          // Poll for result
          if (checkoutId) this.pollStkStatus(checkoutId, 'topup');
        } else { this.topUpMsg = res.data.message || 'Failed'; this.topUpOk = false; this.topUpLoading = false; }
      } catch (e) { this.topUpMsg = 'Connection error'; this.topUpOk = false; this.topUpLoading = false; }
    },
    showWalletSettings(c) {
      this.walletModal = c; this.wMsg = ''; this.showSetPin = false; this.pinMsg = ''; this.newPin = '';
      this.ws = { dailyLimit: c.spend_limit_daily || null, weeklyLimit: c.spend_limit_weekly || null, frozen: c.is_frozen == 1 };
    },
    async setManualPin() {
      if (!this.newPin || this.newPin.length < 4) { this.pinMsg = 'PIN must be at least 4 digits'; this.pinOk = false; return; }
      if (!/^\d+$/.test(this.newPin)) { this.pinMsg = 'PIN must be numbers only'; this.pinOk = false; return; }
      this.pinLoading = true; this.pinMsg = '';
      try {
        const res = await executeJson({ function: 'parentSetStudentPin', studentId: this.walletModal.id, pin: this.newPin }, simitrack);
        if (res.data.success) { this.pinMsg = 'PIN set!'; this.pinOk = true; this.newPin = ''; this.showSetPin = false; }
        else { this.pinMsg = res.data.message || 'Failed'; this.pinOk = false; }
      } catch (e) { this.pinMsg = 'Error'; this.pinOk = false; }
      this.pinLoading = false;
    },
    async saveWallet() {
      this.wLoading = true; this.wMsg = '';
      try {
        const res = await executeJson({ function: 'updateWalletSettings', studentId: this.walletModal.id, dailyLimit: this.ws.dailyLimit || 0, weeklyLimit: this.ws.weeklyLimit || 0, frozen: this.ws.frozen ? 1 : 0 }, simitrack);
        if (res.data.success) { this.wMsg = 'Saved!'; this.wOk = true; this.loadDashboard(); } else { this.wMsg = res.data.message || 'Failed'; this.wOk = false; }
      } catch (e) { this.wMsg = 'Connection error'; this.wOk = false; }
      this.wLoading = false;
    },
    async resetPin() {
      if (!confirm('Reset wallet PIN? A new PIN will be sent via SMS.')) return;
      try {
        const res = await executeJson({ function: 'resetStudentPin', studentId: this.walletModal.id }, simitrack);
        this.wMsg = res.data.success ? 'New PIN sent via SMS!' : (res.data.message || 'Failed'); this.wOk = res.data.success;
      } catch (e) { this.wMsg = 'Error'; this.wOk = false; }
    },
    showReports(c) { this.reportModal = c; this.rMsg = ''; this.reportView = null; this.reportData = null; },
    async viewReport(type) {
      this.reportView = type; this.rLoading = true; this.rMsg = ''; this.reportData = null;
      try {
        const res = await executeJson({ function: 'generateParentReport', studentId: this.reportModal.id, reportType: type }, simitrack);
        if (res.data.success) { this.reportData = res.data.data; }
        else { this.rMsg = res.data.message || 'Failed to load'; this.reportView = null; }
      } catch (e) { this.rMsg = 'Error loading report'; this.reportView = null; }
      this.rLoading = false;
    },
    async dlReport(type, fmt) {
      this.rMsg = 'Preparing download...';
      try {
        let d = this.reportData;
        if (!d || this.reportView !== type) {
          const res = await executeJson({ function: 'generateParentReport', studentId: this.reportModal.id, reportType: type }, simitrack);
          if (!res.data.success) { this.rMsg = res.data.message || 'Failed'; return; }
          d = res.data.data;
        }

        if (fmt === 'csv') { this.generateCSV(type, d); }
        else { this.generatePDF(type, d); }
        this.rMsg = 'Downloaded!';
      } catch (e) { this.rMsg = 'Error loading data'; }
    },
    generateCSV(type, d) {
      let csv = '';
      const s = d.student;
      if (type === 'attendance') {
        csv = `Attendance Report - ${s.name} (${s.admission_no})\nSchool: ${s.school_name}\nClass: ${s.class_name}\nGenerated: ${d.generated_at}\nTotal Days: ${d.summary?.total_days || 0}\n\nDate,Time,Type,Location\n`;
        (d.attendance || []).forEach(a => { const [dt, tm] = (a.tapped_at || '').split(' '); csv += `${dt},${tm},${a.tap_point_type},${(a.tap_point_name || '').replace(/,/g, ';')}\n`; });
      } else if (type === 'wallet') {
        csv = `Wallet Statement - ${s.name} (${s.admission_no})\nSchool: ${s.school_name}\nBalance: KSh ${d.summary?.balance || 0}\nTotal Loaded: KSh ${d.summary?.total_loaded || 0}\nTotal Spent: KSh ${d.summary?.total_spent || 0}\nGenerated: ${d.generated_at}\n\nDate,Type,Category,Amount,Balance After,Description\n`;
        (d.transactions || []).forEach(t => { csv += `${t.created_at},${t.type},${t.category},${t.amount},${t.balance_after},${(t.description || '').replace(/,/g, ';')}\n`; });
      } else if (type === 'fees') {
        csv = `Fee Statement - ${s.name} (${s.admission_no})\nSchool: ${s.school_name}\nTotal Fees: KSh ${d.summary?.total_fees || 0}\nTotal Paid: KSh ${d.summary?.total_paid || 0}\nOutstanding: KSh ${d.summary?.outstanding || 0}\nGenerated: ${d.generated_at}\n\nTerm,Total Fees,Paid,Outstanding\n`;
        (d.balances || []).forEach(b => { csv += `${b.term_name},${b.total_fees},${b.total_paid},${b.outstanding}\n`; });
        csv += `\nPayments\nDate,Amount,M-Pesa Code,Phone\n`;
        (d.payments || []).forEach(p => { csv += `${p.transaction_date},${p.amount},${p.mpesa_code || ''},${p.mpesa_phone || ''}\n`; });
      }
      const blob = new Blob([csv], { type: 'text/csv' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${type}_${d.student.admission_no}.csv`; a.click();
    },
    generatePDF(type, d) {
      const s = d.student;
      // Build HTML content for PDF
      let html = `<div style="font-family:Helvetica,Arial,sans-serif;color:#1a1a1a;max-width:700px;margin:0 auto;padding:20px;">`;

      // Header
      html += `<div style="display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #2E86DE;padding-bottom:12px;margin-bottom:16px;">`;
      html += `<div><h1 style="margin:0;font-size:22px;color:#0D2B5E;">${s.school_name || 'School'}</h1>`;
      if (s.school_address) html += `<p style="margin:2px 0;font-size:11px;color:#666;">${s.school_address}</p>`;
      if (s.school_phone || s.school_email) html += `<p style="margin:2px 0;font-size:11px;color:#666;">${s.school_phone || ''} ${s.school_email ? '| ' + s.school_email : ''}</p>`;
      html += `</div>`;
      html += `<div style="text-align:right;"><span style="font-size:28px;">🎓</span><br><span style="font-size:10px;color:#999;">Powered by Simi Trac</span></div>`;
      html += `</div>`;

      // Title
      const titles = { attendance: 'ATTENDANCE REPORT', wallet: 'WALLET STATEMENT', fees: 'FEE STATEMENT' };
      html += `<h2 style="text-align:center;color:#2E86DE;margin:10px 0;font-size:18px;">${titles[type] || 'REPORT'}</h2>`;

      // Student Info
      html += `<table style="width:100%;margin-bottom:16px;font-size:12px;"><tr>`;
      html += `<td style="padding:4px 8px;background:#f0f4f8;border-radius:4px;"><strong>Student:</strong> ${s.name}</td>`;
      html += `<td style="padding:4px 8px;background:#f0f4f8;border-radius:4px;"><strong>Adm No:</strong> ${s.admission_no}</td>`;
      html += `<td style="padding:4px 8px;background:#f0f4f8;border-radius:4px;"><strong>Class:</strong> ${s.class_name || '-'}</td>`;
      html += `</tr></table>`;

      // Parents
      if (d.parents && d.parents.length > 0) {
        html += `<p style="font-size:11px;color:#666;margin:0 0 12px;">Parent/Guardian: ${d.parents.map(p => p.parent_name + ' (' + p.phone + ')').join(', ')}</p>`;
      }

      const th = 'style="padding:6px 8px;text-align:left;background:#2E86DE;color:#fff;font-size:11px;font-weight:600;border:none;"';
      const td = 'style="padding:5px 8px;font-size:11px;border-bottom:1px solid #eee;"';

      if (type === 'attendance') {
        html += `<p style="font-size:12px;margin-bottom:8px;">Total Days Present: <strong>${d.summary?.total_days || 0}</strong> | Generated: ${d.generated_at}</p>`;
        html += `<table style="width:100%;border-collapse:collapse;"><tr><th ${th}>Date</th><th ${th}>Time</th><th ${th}>Type</th><th ${th}>Location</th></tr>`;
        (d.attendance || []).forEach((a, i) => {
          const [dt, tm] = (a.tapped_at || '').split(' ');
          const bg = i % 2 ? 'background:#f8fafc;' : '';
          const icon = a.tap_point_type === 'gate_entry' ? '🟢' : a.tap_point_type === 'gate_exit' ? '🔴' : '📍';
          html += `<tr style="${bg}"><td ${td}>${dt}</td><td ${td}>${tm}</td><td ${td}>${icon} ${a.tap_point_type}</td><td ${td}>${a.tap_point_name || '-'}</td></tr>`;
        });
        html += `</table>`;
      } else if (type === 'wallet') {
        html += `<div style="display:flex;gap:12px;margin-bottom:12px;">`;
        html += `<div style="flex:1;background:#e6f9f0;padding:10px;border-radius:8px;text-align:center;"><span style="font-size:10px;color:#666;">Balance</span><br><strong style="font-size:16px;color:#10B981;">KSh ${this.formatNum(d.summary?.balance)}</strong></div>`;
        html += `<div style="flex:1;background:#e8f4fd;padding:10px;border-radius:8px;text-align:center;"><span style="font-size:10px;color:#666;">Total Loaded</span><br><strong style="font-size:14px;color:#2E86DE;">KSh ${this.formatNum(d.summary?.total_loaded)}</strong></div>`;
        html += `<div style="flex:1;background:#fef2e8;padding:10px;border-radius:8px;text-align:center;"><span style="font-size:10px;color:#666;">Total Spent</span><br><strong style="font-size:14px;color:#F59E0B;">KSh ${this.formatNum(d.summary?.total_spent)}</strong></div>`;
        html += `</div>`;
        html += `<table style="width:100%;border-collapse:collapse;"><tr><th ${th}>Date</th><th ${th}>Type</th><th ${th}>Description</th><th ${th} style="text-align:right;padding:6px 8px;background:#2E86DE;color:#fff;font-size:11px;">Amount</th><th ${th} style="text-align:right;padding:6px 8px;background:#2E86DE;color:#fff;font-size:11px;">Balance</th></tr>`;
        (d.transactions || []).forEach((t, i) => {
          const bg = i % 2 ? 'background:#f8fafc;' : '';
          const clr = t.type === 'debit' ? 'color:#EF4444;' : 'color:#10B981;';
          const sign = t.type === 'debit' ? '-' : '+';
          html += `<tr style="${bg}"><td ${td}>${t.created_at}</td><td ${td}>${t.type}</td><td ${td}>${t.description || t.category || '-'}</td><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #eee;text-align:right;${clr}font-weight:600;">${sign}KSh ${this.formatNum(t.amount)}</td><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #eee;text-align:right;">KSh ${this.formatNum(t.balance_after)}</td></tr>`;
        });
        html += `</table>`;
      } else if (type === 'fees') {
        html += `<div style="display:flex;gap:12px;margin-bottom:12px;">`;
        html += `<div style="flex:1;background:#f0f4f8;padding:10px;border-radius:8px;text-align:center;"><span style="font-size:10px;color:#666;">Total Fees</span><br><strong style="font-size:16px;">KSh ${this.formatNum(d.summary?.total_fees)}</strong></div>`;
        html += `<div style="flex:1;background:#e6f9f0;padding:10px;border-radius:8px;text-align:center;"><span style="font-size:10px;color:#666;">Paid</span><br><strong style="font-size:16px;color:#10B981;">KSh ${this.formatNum(d.summary?.total_paid)}</strong></div>`;
        html += `<div style="flex:1;background:${d.summary?.outstanding > 0 ? '#fef2f2' : '#e6f9f0'};padding:10px;border-radius:8px;text-align:center;"><span style="font-size:10px;color:#666;">Outstanding</span><br><strong style="font-size:16px;color:${d.summary?.outstanding > 0 ? '#EF4444' : '#10B981'};">KSh ${this.formatNum(d.summary?.outstanding)}</strong></div>`;
        html += `</div>`;
        html += `<h3 style="font-size:14px;color:#0D2B5E;margin:12px 0 6px;">Fee Breakdown by Term</h3>`;
        html += `<table style="width:100%;border-collapse:collapse;"><tr><th ${th}>Term</th><th ${th} style="text-align:right;padding:6px 8px;background:#2E86DE;color:#fff;font-size:11px;">Fees</th><th ${th} style="text-align:right;padding:6px 8px;background:#2E86DE;color:#fff;font-size:11px;">Paid</th><th ${th} style="text-align:right;padding:6px 8px;background:#2E86DE;color:#fff;font-size:11px;">Balance</th></tr>`;
        (d.balances || []).forEach((b, i) => { const bg = i % 2 ? 'background:#f8fafc;' : ''; html += `<tr style="${bg}"><td ${td}>${b.term_name || '-'}</td><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #eee;text-align:right;">KSh ${this.formatNum(b.total_fees)}</td><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #eee;text-align:right;color:#10B981;">KSh ${this.formatNum(b.total_paid)}</td><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #eee;text-align:right;color:${b.outstanding > 0 ? '#EF4444' : '#10B981'};">KSh ${this.formatNum(b.outstanding)}</td></tr>`; });
        html += `</table>`;
        if (d.payments && d.payments.length > 0) {
          html += `<h3 style="font-size:14px;color:#0D2B5E;margin:16px 0 6px;">Payment History</h3>`;
          html += `<table style="width:100%;border-collapse:collapse;"><tr><th ${th}>Date</th><th ${th} style="text-align:right;padding:6px 8px;background:#2E86DE;color:#fff;font-size:11px;">Amount</th><th ${th}>M-Pesa Code</th><th ${th}>Reference</th></tr>`;
          d.payments.forEach((p, i) => { const bg = i % 2 ? 'background:#f8fafc;' : ''; html += `<tr style="${bg}"><td ${td}>${p.transaction_date}</td><td style="padding:5px 8px;font-size:11px;border-bottom:1px solid #eee;text-align:right;color:#10B981;font-weight:600;">KSh ${this.formatNum(p.amount)}</td><td ${td}>${p.mpesa_code || '-'}</td><td ${td}>${p.account_reference || '-'}</td></tr>`; });
          html += `</table>`;
        }
      }

      // Footer
      html += `<div style="margin-top:24px;padding-top:12px;border-top:2px solid #eee;text-align:center;font-size:10px;color:#999;">`;
      html += `Generated on ${d.generated_at} | ${s.school_name} | Powered by Simi Trac — simitrac.co.ke`;
      html += `</div></div>`;

      // Open in new window for print/save as PDF
      const win = window.open('', '_blank');
      win.document.write(`<!DOCTYPE html><html><head><title>${titles[type]} - ${s.name}</title><style>@media print{body{margin:0;padding:12px;}}</style></head><body>${html}</body></html>`);
      win.document.close();
      setTimeout(() => win.print(), 500);
    },
    showFees(c) { this.feesModal = c; this.feeMsg = ''; this.feeAmt = c.fees?.balance || 0; },
    async doPayFees() {
      if (!this.feeAmt || this.feeAmt < 1) { this.feeMsg = 'Enter amount'; this.feeOk = false; return; }
      if (!this.feePhone || this.feePhone.length < 9) { this.feeMsg = 'Enter valid phone'; this.feeOk = false; return; }
      this.feeLoading = true; this.feeMsg = '';
      try {
        const res = await executeJson({ function: 'feePaymentStk', studentId: this.feesModal.id, amount: this.feeAmt, phone: this.feePhone }, simitrack);
        if (res.data.success) {
          const checkoutId = res.data.data?.checkout_request_id;
          this.feeMsg = '📱 M-Pesa prompt sent! Enter your PIN...';
          this.feeOk = true;
          this.feeLoading = false;
          if (checkoutId) this.pollStkStatus(checkoutId, 'fees');
        } else { this.feeMsg = res.data.message || 'Failed'; this.feeOk = false; this.feeLoading = false; }
      } catch (e) { this.feeMsg = 'Connection error'; this.feeOk = false; this.feeLoading = false; }
    },
    pollStkStatus(checkoutId, type) {
      let attempts = 0;
      const maxAttempts = 20; // 60 seconds max
      const poll = setInterval(async () => {
        attempts++;
        try {
          const r = await executeJson({ function: 'checkStkStatus', checkoutRequestId: checkoutId }, simitrack);
          const status = r.data.data?.status;
          const msg = r.data.message;

          if (status === 'completed') {
            if (type === 'topup') { this.topUpMsg = msg; this.topUpOk = true; }
            else { this.feeMsg = msg; this.feeOk = true; }
            clearInterval(poll);
            setTimeout(() => this.loadDashboard(), 1500);
          } else if (status === 'failed' || status === 'cancelled') {
            if (type === 'topup') { this.topUpMsg = msg; this.topUpOk = false; }
            else { this.feeMsg = msg; this.feeOk = false; }
            clearInterval(poll);
          } else if (attempts >= maxAttempts) {
            const timeoutMsg = '⏳ No response yet. If you entered your PIN, the payment may still process.';
            if (type === 'topup') { this.topUpMsg = timeoutMsg; this.topUpOk = false; }
            else { this.feeMsg = timeoutMsg; this.feeOk = false; }
            clearInterval(poll);
          } else {
            const waitMsg = `⏳ Waiting for M-Pesa... (${attempts * 3}s)`;
            if (type === 'topup') this.topUpMsg = waitMsg;
            else this.feeMsg = waitMsg;
          }
        } catch(e) { /* keep polling */ }
      }, 3000);
    },
    getWeeklySpending(child) {
      const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
      const now = new Date(); const week = Array(7).fill(0);
      (child.recent_transactions || []).forEach(t => { if (t.type !== 'debit') return; const d = new Date(t.created_at); if ((now - d) / 86400000 < 7) week[d.getDay()] += parseFloat(t.amount) || 0; });
      const mx = Math.max(...week, 1);
      return week.map((v, i) => ({ label: days[i], total: Math.round(v), pct: Math.round(v / mx * 100) }));
    },
    showPhoto(c) { this.photoModal = c; },
    showParentPhotoFull() { if (this.parentPhoto) this.parentPhotoFull = true; },
    formatNum(v) { return (parseFloat(v) || 0).toLocaleString(); },
    formatTime(dt) { if (!dt) return ''; const p = dt.split(' ')[1]?.split(':'); if (!p) return ''; const h = +p[0]; return `${h % 12 || 12}:${p[1]} ${h >= 12 ? 'PM' : 'AM'}`; },
    formatDate(dt) { return dt ? dt.substring(5, 16) : ''; },
    logout() { sessionStorage.clear(); localStorage.removeItem('authToken'); localStorage.removeItem('rememberMe'); this.$router.push('/login'); }
  }
};
</script>

<style scoped>
.pd-page{background:#0A1628;min-height:100vh;color:#E8EDF4}
.pd-header{background:#0D2B5E;padding:14px 20px;position:sticky;top:0;z-index:10}
.pd-header-inner{max-width:600px;margin:0 auto;display:flex;justify-content:space-between;align-items:center}
.pd-header-left{display:flex;align-items:center;gap:10px}
.pd-parent-photo{position:relative;width:40px;height:40px;cursor:pointer;flex-shrink:0}
.pd-parent-photo img{width:40px;height:40px;border-radius:50%;object-fit:cover}
.pd-parent-overlay{position:absolute;top:0;left:0;width:40px;height:40px;border-radius:50%;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;opacity:0;transition:.2s;font-size:16px;cursor:pointer}
.pd-parent-photo:hover .pd-parent-overlay{opacity:1}
.pd-avatar-sm{width:40px;height:40px;border-radius:50%;background:#1A3A6A;display:flex;align-items:center;justify-content:center;font-size:16px;color:#4FC3F7;font-weight:700;cursor:pointer}
.pd-photo-edit{position:absolute;bottom:-2px;right:-2px;background:#2E86DE;width:18px;height:18px;border-radius:50%;font-size:10px;display:flex;align-items:center;justify-content:center;border:2px solid #0D2B5E}
.pd-header-actions{display:flex;gap:8px}
.pd-title{margin:0;font-size:20px}.pd-subtitle{margin:0;font-size:12px;color:#8BA4C4}
.pd-logout{background:0;border:1px solid #EF4444;color:#EF4444;padding:6px 14px;border-radius:6px;cursor:pointer;font-size:12px}
.pd-body{max-width:600px;margin:0 auto;padding:16px}
.pd-loading,.pd-empty{text-align:center;padding:40px;color:#8BA4C4}
.pd-child-card{background:#0F2847;border-radius:12px;padding:16px;margin-bottom:16px}
.pd-child-header{display:flex;align-items:center;gap:12px;margin-bottom:12px}
.pd-child-photo-wrap{position:relative;flex-shrink:0}
.pd-child-photo{position:relative;width:48px;height:48px}
.pd-child-photo img{width:48px;height:48px;border-radius:50%;object-fit:cover}
.pd-clickable{cursor:pointer}
.pd-photo-overlay{position:absolute;top:0;left:0;width:48px;height:48px;border-radius:50%;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;opacity:0;transition:.2s;font-size:18px}
.pd-clickable:hover .pd-photo-overlay{opacity:1}
.pd-avatar{width:48px;height:48px;border-radius:50%;background:#1A3A6A;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700;color:#4FC3F7}
.pd-photo-upload-btn{position:absolute;bottom:-4px;right:-4px;background:#2E86DE;border:2px solid #0F2847;color:#fff;width:22px;height:22px;border-radius:50%;font-size:11px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;z-index:2}
.pd-photo-upload-btn:hover{background:#10B981}
.pd-child-info{flex:1}.pd-child-name{margin:0;font-size:16px;font-weight:600}.pd-child-class{font-size:12px;color:#8BA4C4}
.pd-wallet-badge{text-align:right}.pd-wallet-label{display:block;font-size:10px;color:#8BA4C4;text-transform:uppercase}.pd-wallet-amount{font-size:18px;font-weight:700;color:#10B981}
.pd-actions{display:flex;gap:6px;margin-bottom:12px;flex-wrap:wrap}
.pd-action-btn{flex:1;min-width:70px;padding:8px 6px;border:none;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;text-align:center}
.pd-sponsored-chip{flex:1;min-width:70px;padding:8px 6px;border-radius:8px;font-size:12px;font-weight:600;text-align:center;background:rgba(245,158,11,.12);color:#F59E0B;border:1px dashed rgba(245,158,11,.4);cursor:help;display:flex;align-items:center;justify-content:center;}
.pd-green{background:rgba(16,185,129,.15);color:#10B981}.pd-blue{background:rgba(46,134,222,.15);color:#4FC3F7}.pd-purple{background:rgba(139,92,246,.15);color:#8B5CF6}.pd-orange{background:rgba(245,158,11,.15);color:#F59E0B}
.pd-action-btn:hover{filter:brightness(1.2)}.pd-full{width:100%;flex:unset}
.pd-section{margin-top:12px;padding-top:12px;border-top:1px solid #1A3A6A}
.pd-section-title{margin:0 0 8px;font-size:13px;color:#4FC3F7;font-weight:600}
.pd-att-list{display:flex;flex-direction:column;gap:4px}.pd-att-item{display:flex;align-items:center;gap:8px;font-size:13px}.pd-att-point{flex:1}.pd-att-time{color:#8BA4C4;font-size:12px}
.pd-chart{display:flex;gap:4px;align-items:flex-end;height:80px;padding:0 8px}
.pd-chart-col{flex:1;display:flex;flex-direction:column;align-items:center;height:100%;justify-content:flex-end}
.pd-chart-bar{width:100%;max-width:28px;background:#1A3A6A;border-radius:4px 4px 0 0;min-height:2px;transition:.3s}.pd-bar-fill{background:#4FC3F7}
.pd-chart-lbl{font-size:10px;color:#8BA4C4;margin-top:4px}.pd-chart-val{font-size:9px;color:#4FC3F7}
.pd-fees-row{display:flex;gap:8px}.pd-fee-item{flex:1;background:#0A1628;padding:8px;border-radius:8px;text-align:center}
.pd-fee-label{display:block;font-size:10px;color:#8BA4C4;text-transform:uppercase}.pd-fee-val{font-size:14px;font-weight:600}
.pd-txn-list{display:flex;flex-direction:column;gap:4px}.pd-txn-item{display:flex;align-items:center;gap:6px;font-size:12px}
.pd-txn-desc{flex:1}.pd-txn-amt{font-weight:600;min-width:70px;text-align:right}.pd-txn-date{color:#8BA4C4;font-size:11px;min-width:60px;text-align:right}
.pd-c-green{color:#10B981}.pd-c-red{color:#EF4444}.pd-muted{color:#8BA4C4}.pd-small{font-size:12px}
.mt-2{margin-top:8px}.mt-3{margin-top:12px}.tc{text-align:center}
.pd-footer{text-align:center;padding:24px;font-size:12px;color:#4A6A8A}.pd-footer strong{color:#4FC3F7}
/* Modals */
.pd-overlay{position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.8);z-index:1000;display:flex;align-items:center;justify-content:center;padding:20px}
.pd-modal{position:relative;max-width:400px;width:100%;background:#0F2847;border-radius:16px;padding:24px;max-height:90vh;overflow-y:auto}
.pd-x{position:absolute;top:12px;right:12px;background:rgba(255,255,255,.1);border:none;color:#fff;width:32px;height:32px;border-radius:50%;font-size:16px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.pd-x:hover{background:#EF4444}
.pd-mtitle{margin:0 0 4px;font-size:18px}
.pd-modal-photo{padding:0;overflow:hidden}.pd-img-full{width:100%;aspect-ratio:1;object-fit:cover;display:block}
.pd-img-cap{padding:16px;text-align:center}.pd-img-cap h5{margin:0}.pd-img-cap p{margin:4px 0 0;color:#8BA4C4;font-size:13px}
.pd-fg{margin-bottom:12px}.pd-fg label{display:block;font-size:12px;color:#8BA4C4;margin-bottom:4px}
.pd-inp{width:100%;background:#0A1628;border:1px solid #1A3A6A;color:#E8EDF4;padding:10px 12px;border-radius:8px;font-size:15px;box-sizing:border-box}
.pd-inp:focus{border-color:#4FC3F7;outline:none}
.pd-inp-sm{width:120px;background:#0A1628;border:1px solid #1A3A6A;color:#E8EDF4;padding:6px 10px;border-radius:6px;font-size:14px;text-align:right}
.pd-quicks{display:flex;gap:6px;margin-bottom:12px}
.pd-qbtn{flex:1;padding:8px;background:#1A3A6A;border:none;color:#4FC3F7;border-radius:6px;cursor:pointer;font-size:13px;font-weight:600}
.pd-qbtn:hover{background:#2E86DE;color:#fff}
.pd-btn{padding:12px;border:none;border-radius:8px;font-weight:600;font-size:15px;cursor:pointer;display:block;text-align:center}
.pd-btn-green{background:#10B981;color:#fff}.pd-btn-green:hover{background:#0d9668}
.pd-btn-blue{background:#2E86DE;color:#fff}
.pd-btn-sm{padding:6px 12px;font-size:12px;border-radius:6px;cursor:pointer;border:none}
.pd-btn-outline{background:0;border:1px solid #4FC3F7;color:#4FC3F7}
.pd-btn:disabled{opacity:.5}
.pd-pin-actions{display:flex;gap:6px}
.pd-pin-set{padding:10px 0;display:flex;flex-direction:column;gap:6px}
.pd-pin-row{display:flex;gap:8px;align-items:center}
.pd-btn-green-sm{background:#10B981;color:#fff;padding:6px 14px;font-size:12px;border:none;border-radius:6px;cursor:pointer;font-weight:600}
.pd-btn-green-sm:hover{background:#0d9668}
.pd-srow{display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-bottom:1px solid #1A3A6A;font-size:14px}
.pd-sw{position:relative;width:44px;height:24px}.pd-sw input{opacity:0;width:0;height:0}
.pd-sw-s{position:absolute;top:0;left:0;right:0;bottom:0;background:#1A3A6A;border-radius:24px;cursor:pointer;transition:.3s}
.pd-sw-s:before{content:'';position:absolute;height:18px;width:18px;left:3px;bottom:3px;background:#8BA4C4;border-radius:50%;transition:.3s}
.pd-sw input:checked+.pd-sw-s{background:#EF4444}.pd-sw input:checked+.pd-sw-s:before{transform:translateX(20px);background:#fff}
.pd-rlist{display:flex;flex-direction:column;gap:8px;margin-top:12px}
.pd-rbtn{background:#0A1628;border:1px solid #1A3A6A;color:#E8EDF4;padding:12px;border-radius:8px;cursor:pointer;text-align:left;font-size:14px}
.pd-rbtn:hover{border-color:#4FC3F7;background:#1A3A6A}
/* School Badge */
.pd-school-badge{background:#1A3A6A;color:#4FC3F7;font-size:11px;font-weight:600;padding:3px 10px;border-radius:6px;margin-bottom:8px;display:inline-block}
/* Report Modal */
.pd-modal-report{max-width:520px;max-height:92vh}
.pd-rtabs{display:flex;flex-direction:column;gap:8px;margin-top:12px}
.pd-rtab{background:#0A1628;border:1px solid #1A3A6A;color:#E8EDF4;padding:14px;border-radius:10px;cursor:pointer;text-align:left;display:flex;align-items:center;gap:12px;transition:.2s}
.pd-rtab:hover{border-color:#4FC3F7;background:#1A3A6A}
.pd-rtab-icon{font-size:24px;flex-shrink:0}
.pd-rtab-label{font-size:14px;font-weight:600;display:block}
.pd-rtab-sub{font-size:11px;color:#8BA4C4;display:block}
.pd-rloading{text-align:center;padding:32px;color:#8BA4C4}
.pd-spinner{width:28px;height:28px;border:3px solid #1A3A6A;border-top-color:#4FC3F7;border-radius:50%;animation:pd-spin .7s linear infinite;margin:0 auto 8px}
@keyframes pd-spin{to{transform:rotate(360deg)}}
.pd-report-toolbar{display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;padding-bottom:10px;border-bottom:1px solid #1A3A6A}
.pd-report-dl{display:flex;gap:6px}
.pd-rv-body{max-height:55vh;overflow-y:auto;padding-right:4px}
.pd-rv-summary{display:flex;gap:8px;margin-bottom:12px;flex-wrap:wrap}
.pd-rv-stat{flex:1;min-width:80px;background:#0A1628;padding:8px;border-radius:8px;text-align:center}
.pd-rv-stat span{display:block;font-size:10px;color:#8BA4C4;text-transform:uppercase}
.pd-rv-stat strong{font-size:15px;display:block;margin-top:2px}
.pd-rv-green{border-left:3px solid #10B981}.pd-rv-green strong{color:#10B981}
.pd-rv-blue{border-left:3px solid #4FC3F7}.pd-rv-blue strong{color:#4FC3F7}
.pd-rv-orange{border-left:3px solid #F59E0B}.pd-rv-orange strong{color:#F59E0B}
.pd-rv-red{border-left:3px solid #EF4444}.pd-rv-red strong{color:#EF4444}
.pd-rv-table{margin-bottom:8px}
.pd-rv-thead{display:flex;gap:4px;padding:6px 8px;background:#1A3A6A;border-radius:6px 6px 0 0;font-size:10px;font-weight:700;color:#4FC3F7;text-transform:uppercase}
.pd-rv-thead span{flex:1;min-width:0}
.pd-rv-trow{display:flex;gap:4px;padding:5px 8px;font-size:12px;border-bottom:1px solid #1A3A6A22}
.pd-rv-trow span{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.pd-rv-alt{background:rgba(26,58,106,.15)}
.pd-rv-date{color:#8BA4C4;font-size:11px}
.pd-rv-desc{font-size:11px}
.pd-rv-amt{font-weight:600;text-align:right;font-size:12px}
.pd-rv-sub{margin:12px 0 6px;font-size:13px;color:#4FC3F7;font-weight:600}
</style>
